import { useState } from "react";
import { Link } from "react-router-dom";

import { DownloadIcon } from "./DownloadIcon";
import { Markdown } from "./Markdown";
import { Card, InlineNote, Tag } from "./primitives";
import type { MemoryReference, MemorySummary } from "../lib/types";

/** What a memory item is, as a reader calls it. */
export const KIND_LABEL: Record<string, string> = {
  entity: "entity",
  metric: "figure",
  relation: "relationship",
  outcome: "engagement",
  chunk: "document",
  statement: "testimony",
};

export function hostOf(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, "");
  } catch {
    return url;
  }
}

/** The briefing, its citations as links to the references under it. */
export function Briefing({
  summary,
  inGraph = false,
}: {
  summary: MemorySummary;
  /** Admins only: the item searched in the console, which they alone have. */
  inGraph?: boolean;
}) {
  const [shown, setShown] = useState<string | null>(null);
  const cited = summary.references.filter((ref) => ref.cited);
  const uncited = summary.references.length - cited.length;

  const show = (n: number) => {
    const id = `M${n}`;
    setShown(id);
    document
      .getElementById(`memory-ref-${id}`)
      ?.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  const download = () => {
    const blob = new Blob([summary.markdown], { type: "text/markdown;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = `${slug(summary.question) || "memory-summary"}.md`;
    anchor.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Card
      title="Summary from memory"
      action={
        // Small and muted, as a file's download is in a run's list: the
        // briefing is what the card is for, saving it is the aside.
        <button
          type="button"
          onClick={download}
          title="Download as Markdown (.md)"
          aria-label="Download the summary as Markdown"
          className="rounded-sm p-1.5 text-muted hover:bg-surface-2 hover:text-ink focus-visible:text-ink"
        >
          <DownloadIcon />
        </button>
      }
    >
      <div className="space-y-3 px-4 py-3">
        {summary.unknown.length > 0 && (
          <InlineNote tone="warning">
            Cites {summary.unknown.join(", ")}, which it was never given — the
            sentences carrying {summary.unknown.length === 1 ? "that citation" : "those"} are
            not backed by the graph.
          </InlineNote>
        )}
        <Markdown content={summary.body} citationPrefix="M" onCitationClick={show} />
        <p className="text-xs text-muted">
          {summary.material} items read · {cited.length} cited
          {uncited > 0 ? ` · ${uncited} not used` : ""} · cost $
          {summary.cost_usd.toFixed(2)}
        </p>
      </div>
      {cited.length > 0 && (
        <section>
          <h3 className="label border-b border-t border-line bg-surface-2 px-4 py-1.5">
            References
          </h3>
          <ol className="divide-y divide-line">
            {cited.map((ref) => (
              <ReferenceRow
                key={ref.id}
                reference={ref}
                shown={shown === ref.id}
                inGraph={inGraph}
              />
            ))}
          </ol>
        </section>
      )}
    </Card>
  );
}

function ReferenceRow({
  reference,
  shown,
  inGraph,
}: {
  reference: MemoryReference;
  shown: boolean;
  inGraph: boolean;
}) {
  return (
    <li
      id={`memory-ref-${reference.id}`}
      className={`px-4 py-2 ${shown ? "bg-surface-2" : ""}`}
    >
      <div className="flex flex-wrap items-baseline gap-2">
        <span className="font-mono text-xs text-muted">{reference.id}</span>
        <Tag>{KIND_LABEL[reference.kind] ?? reference.kind}</Tag>
        <span className="min-w-0 text-sm text-ink">{reference.title}</span>
      </div>
      <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs">
        {reference.sources.map((source) =>
          /^https?:\/\//.test(source.url) ? (
            <a
              key={source.url}
              href={source.url}
              target="_blank"
              rel="noreferrer"
              className="text-accent underline-offset-2 hover:underline"
            >
              {source.title || hostOf(source.url)}
            </a>
          ) : (
            <span key={source.title} className="text-muted">
              {source.title}
            </span>
          ),
        )}
        {reference.run_id && (
          <Link
            to={`/engagements/${encodeURIComponent(reference.run_id)}`}
            className="text-accent"
          >
            open run
          </Link>
        )}
        {/* The item in the graph: the admin console, asked about it, in a new
            tab so the briefing stays where it is. Nobody else has the console. */}
        {inGraph && (
          <a
            href={`/admin/memory?q=${encodeURIComponent(reference.title)}`}
            target="_blank"
            rel="noreferrer"
            className="text-accent"
            title={reference.key}
          >
            in graph
          </a>
        )}
      </div>
    </li>
  );
}

function slug(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9äöüß]+/g, "-")
    .replace(/^-|-$/g, "")
    .slice(0, 60);
}
